import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Shared severity styling for card-shaped surfaces (Toast, BannerStack). 'success' is
// only used by Toast; callers whose severity type lacks it (e.g. BannerStack's
// BannerSeverity) simply never index into it.
export type Severity = 'success' | 'info' | 'warning' | 'error';

// `card` / `icon` style the light card surface (BannerStack); `accent` and `onDark` the dark Toast.
export const SEVERITY_TONE: Record<Severity, { card: string; icon: string; accent: string; onDark: string; Icon: LucideIcon }> = {
    success: {
        card: 'bg-state-success/15 border-state-success/35 border-l-state-success',
        icon: 'text-state-success',
        accent: 'border-l-emerald-400',
        onDark: 'text-emerald-400',
        Icon: CheckCircle,
    },
    info: {
        card: 'bg-brand/10 border-brand/30 border-l-brand',
        icon: 'text-brand-dark',
        accent: 'border-l-brand',
        onDark: 'text-brand',
        Icon: Info,
    },
    warning: {
        card: 'bg-state-warning/20 border-state-warning/50 border-l-state-warning',
        icon: 'text-[#B8860B]',
        accent: 'border-l-amber-400',
        onDark: 'text-amber-400',
        Icon: AlertTriangle,
    },
    error: {
        card: 'bg-state-error/15 border-state-error/40 border-l-state-error',
        icon: 'text-state-error',
        accent: 'border-l-red-400',
        onDark: 'text-red-400',
        Icon: AlertCircle,
    },
};

export function DismissButton({ onClick, title, dark }: { onClick: () => void; title?: string; dark?: boolean }) {
    const tone = dark
        ? 'text-white/50 hover:bg-white/10 hover:text-white'
        : 'text-coolslate hover:bg-graphite/5 hover:text-graphite';
    return (
        <button
            type="button"
            onClick={onClick}
            className={`shrink-0 -mr-0.5 p-1 rounded-md ${tone} transition-colors`}
            aria-label="Dismiss"
            title={title}
        >
            <X className="w-4 h-4" />
        </button>
    );
}
